import {
  IChargeUnit,
  IMonthNum,
  ISeasonMapping,
  ISeasonalNum,
  ISeasonalPVCharge,
  ITimeDataItem,
  ITouPeriods,
} from './algoInterface'
import { calKepcoMonthlyChargeAsUsage } from './calCharge'
import {
  calMonthlyExCharge,
  calSeasonalExCharge,
  IMonthNumPlusIMonthNum,
} from './calExCharge'
import { calMonthSum, mulTimeData } from './classifyTimeData'
import { monthSumToSeasonSum } from './monthToSeason'
import {
  buildPeakDeductionLoadData,
  sumBuyCostGeneration,
} from './peakDeductionSellPlan'

export interface SellPlanUnitPricePoint {
  purchaseAverageUnit: number
  totalProfit: number
}

interface BuildSellPlanUnitPriceCurveParams {
  loadData: ITimeDataItem[]
  seasonalLoadSum: ISeasonalNum
  buyCost: ISeasonalPVCharge
  gridUsageChargeUnitSum: number
  kepcoSeasonCharge: ISeasonalNum
  touPeriods: ITouPeriods
  kepcoChargeUnit: IChargeUnit
  kepcoDefaultChargeUnit: number
  afterKEPCOContractedPower: number
  essPowerRate: number
  environmentalUnit: number
  greenPremiumUnit: number
  re100Opt: string
  seasonMapping: ISeasonMapping
  holidayData: number[]
}

interface BuildPeakDeductionSellPlanUnitPriceCurveParams {
  loadData: ITimeDataItem[]
  buyCost: ISeasonalPVCharge
  touPeriods: ITouPeriods
  kepcoChargeUnit: IChargeUnit
  kepcoDefaultChargeUnit: number
  contractedPower: number
  environmentalUnit: number
  greenPremiumUnit: number
  re100Opt: string
  networkUnit: number
  seasonMapping: ISeasonMapping
  holidayData: number[]
}

export interface SellPlanUnitPriceCurve {
  points: SellPlanUnitPricePoint[]
  currentPurchaseAverageUnit: number
  currentTotalProfit: number
  currentTotalProfitRatio: number | null
  unitPriceLimit: number | null
  xMaxUnit: number
  feasible: boolean
}

const EMPTY_SEASONAL_NUM: ISeasonalNum = {
  spr: 0,
  sum: 0,
  fall: 0,
  win: 0,
}

const EMPTY_MONTH_NUM: IMonthNum = {
  1: 0,
  2: 0,
  3: 0,
  4: 0,
  5: 0,
  6: 0,
  7: 0,
  8: 0,
  9: 0,
  10: 0,
  11: 0,
  12: 0,
}

const sumMonthNum = (value: IMonthNum) =>
  value[1] +
  value[2] +
  value[3] +
  value[4] +
  value[5] +
  value[6] +
  value[7] +
  value[8] +
  value[9] +
  value[10] +
  value[11] +
  value[12]

const sumSeasonalNum = (value: ISeasonalNum) =>
  value.spr + value.sum + value.fall + value.win

const addSeasonalNum = (
  left: ISeasonalNum,
  right: ISeasonalNum
): ISeasonalNum => ({
  spr: left.spr + right.spr,
  sum: left.sum + right.sum,
  fall: left.fall + right.fall,
  win: left.win + right.win,
})

const subtractSeasonalNum = (
  left: ISeasonalNum,
  right: ISeasonalNum
): ISeasonalNum => ({
  spr: left.spr - right.spr,
  sum: left.sum - right.sum,
  fall: left.fall - right.fall,
  win: left.win - right.win,
})

const getCurrentPurchaseAverageUnit = (buyCost: ISeasonalPVCharge) => {
  const totalPurchaseCharge =
    buyCost.spr.charge +
    buyCost.sum.charge +
    buyCost.fall.charge +
    buyCost.win.charge
  const totalPurchaseGeneration = sumBuyCostGeneration(buyCost)

  return totalPurchaseGeneration === 0
    ? 0
    : totalPurchaseCharge / totalPurchaseGeneration
}

export const buildSellPlanUnitPriceCurve = ({
  loadData,
  seasonalLoadSum,
  buyCost,
  gridUsageChargeUnitSum,
  kepcoSeasonCharge,
  touPeriods,
  kepcoChargeUnit,
  kepcoDefaultChargeUnit,
  afterKEPCOContractedPower,
  essPowerRate,
  environmentalUnit,
  greenPremiumUnit,
  re100Opt,
  seasonMapping,
  holidayData,
}: BuildSellPlanUnitPriceCurveParams): SellPlanUnitPriceCurve => {
  const currentPurchaseAverageUnit = getCurrentPurchaseAverageUnit(buyCost)

  if (
    loadData.length === 0 ||
    essPowerRate <= 0 ||
    afterKEPCOContractedPower < 0 ||
    sumBuyCostGeneration(buyCost) <= 0
  ) {
    return {
      points: [],
      currentPurchaseAverageUnit,
      currentTotalProfit: 0,
      currentTotalProfitRatio: null,
      unitPriceLimit: null,
      xMaxUnit: Math.max(currentPurchaseAverageUnit * 2, 1),
      feasible: false,
    }
  }

  const kepcoLoadData = mulTimeData(loadData, 1 - essPowerRate / 100)
  const seasonalKepcoUsage = monthSumToSeasonSum(
    calMonthSum(kepcoLoadData),
    seasonMapping
  )
  const totalPurchaseGeneration = sumBuyCostGeneration(buyCost)

  if (totalPurchaseGeneration <= 0) {
    return {
      points: [],
      currentPurchaseAverageUnit,
      currentTotalProfit: 0,
      currentTotalProfitRatio: null,
      unitPriceLimit: null,
      xMaxUnit: Math.max(currentPurchaseAverageUnit * 2, 1),
      feasible: false,
    }
  }

  const beforeGreenPremiumCharge =
    re100Opt === 'achieve'
      ? calSeasonalExCharge(greenPremiumUnit, seasonalLoadSum)
      : EMPTY_SEASONAL_NUM
  const beforeCustomerCharge =
    re100Opt === 'achieve'
      ? addSeasonalNum(kepcoSeasonCharge, beforeGreenPremiumCharge)
      : kepcoSeasonCharge
  const afterKepcoMonthlyCharge = calKepcoMonthlyChargeAsUsage(
    kepcoLoadData,
    touPeriods,
    kepcoChargeUnit,
    seasonMapping,
    holidayData,
    kepcoDefaultChargeUnit,
    afterKEPCOContractedPower,
    environmentalUnit
  )
  const afterKepcoCharge = monthSumToSeasonSum(
    afterKepcoMonthlyCharge,
    seasonMapping
  )
  const remainingGreenPremiumCharge =
    re100Opt === 'achieve'
      ? calSeasonalExCharge(greenPremiumUnit, seasonalKepcoUsage)
      : EMPTY_SEASONAL_NUM
  const afterKepcoOnlyCustomerCharge = addSeasonalNum(
    afterKepcoCharge,
    remainingGreenPremiumCharge
  )
  const maxEssCharge = subtractSeasonalNum(
    beforeCustomerCharge,
    afterKepcoOnlyCustomerCharge
  )
  const gridUsageCharge = sumSeasonalNum(
    calSeasonalExCharge(gridUsageChargeUnitSum, seasonalLoadSum)
  )
  const recoverableBeforePurchase =
    sumSeasonalNum(maxEssCharge) - gridUsageCharge
  const unitPriceLimit = recoverableBeforePurchase / totalPurchaseGeneration
  const xMaxUnit =
    unitPriceLimit > 0
      ? Math.max(unitPriceLimit * 1.5, currentPurchaseAverageUnit)
      : Math.max(currentPurchaseAverageUnit * 2, 1)
  const maxUnitPoint = Math.max(1, Math.floor(xMaxUnit))
  const points: SellPlanUnitPricePoint[] = []

  for (
    let purchaseAverageUnit = 0;
    purchaseAverageUnit <= maxUnitPoint;
    purchaseAverageUnit++
  ) {
    const totalProfit = Math.floor(
      recoverableBeforePurchase - totalPurchaseGeneration * purchaseAverageUnit
    )

    points.push({
      purchaseAverageUnit,
      totalProfit,
    })
  }

  const currentTotalProfit = Math.floor(
    recoverableBeforePurchase -
      totalPurchaseGeneration * currentPurchaseAverageUnit
  )
  const previousCustomerCharge = sumSeasonalNum(beforeCustomerCharge)
  const currentTotalProfitRatio =
    previousCustomerCharge === 0
      ? null
      : (currentTotalProfit / previousCustomerCharge) * 100

  return {
    points,
    currentPurchaseAverageUnit,
    currentTotalProfit,
    currentTotalProfitRatio,
    unitPriceLimit,
    xMaxUnit,
    feasible: currentTotalProfit >= 0,
  }
}

export const buildPeakDeductionSellPlanUnitPriceCurve = ({
  loadData,
  buyCost,
  touPeriods,
  kepcoChargeUnit,
  kepcoDefaultChargeUnit,
  contractedPower,
  environmentalUnit,
  greenPremiumUnit,
  re100Opt,
  networkUnit,
  seasonMapping,
  holidayData,
}: BuildPeakDeductionSellPlanUnitPriceCurveParams): SellPlanUnitPriceCurve => {
  const currentPurchaseAverageUnit = getCurrentPurchaseAverageUnit(buyCost)
  const totalPurchaseGeneration = sumBuyCostGeneration(buyCost)

  if (
    loadData.length === 0 ||
    contractedPower <= 0 ||
    totalPurchaseGeneration <= 0
  ) {
    return {
      points: [],
      currentPurchaseAverageUnit,
      currentTotalProfit: 0,
      currentTotalProfitRatio: null,
      unitPriceLimit: null,
      xMaxUnit: Math.max(currentPurchaseAverageUnit * 2, 1),
      feasible: false,
    }
  }

  const splitLoadData = buildPeakDeductionLoadData({
    loadData,
    totalPvGeneration: totalPurchaseGeneration,
    seasonMapping,
    holidayData,
    touPeriods,
  })

  if (splitLoadData.actualDeductedKwh <= 0) {
    return {
      points: [],
      currentPurchaseAverageUnit,
      currentTotalProfit: 0,
      currentTotalProfitRatio: null,
      unitPriceLimit: null,
      xMaxUnit: Math.max(currentPurchaseAverageUnit * 2, 1),
      feasible: false,
    }
  }

  const beforeKepcoCharge = calKepcoMonthlyChargeAsUsage(
    loadData,
    touPeriods,
    kepcoChargeUnit,
    seasonMapping,
    holidayData,
    kepcoDefaultChargeUnit,
    contractedPower,
    environmentalUnit
  )
  const afterKepcoCharge = calKepcoMonthlyChargeAsUsage(
    splitLoadData.kepcoLoadData,
    touPeriods,
    kepcoChargeUnit,
    seasonMapping,
    holidayData,
    kepcoDefaultChargeUnit,
    contractedPower,
    environmentalUnit
  )
  const beforeGreenPremiumCharge =
    re100Opt === 'achieve'
      ? calMonthlyExCharge(greenPremiumUnit, calMonthSum(loadData))
      : EMPTY_MONTH_NUM
  const remainingGreenPremiumCharge =
    re100Opt === 'achieve'
      ? calMonthlyExCharge(
          greenPremiumUnit,
          calMonthSum(splitLoadData.kepcoLoadData)
        )
      : EMPTY_MONTH_NUM
  const beforeCustomerCharge = IMonthNumPlusIMonthNum(
    beforeKepcoCharge,
    beforeGreenPremiumCharge
  )
  const afterKepcoOnlyCustomerCharge = IMonthNumPlusIMonthNum(
    afterKepcoCharge,
    remainingGreenPremiumCharge
  )
  const maxCustomerPayment =
    sumMonthNum(beforeCustomerCharge) -
    sumMonthNum(afterKepcoOnlyCustomerCharge)
  const networkCost = splitLoadData.actualDeductedKwh * networkUnit
  const recoverableBeforePurchase = maxCustomerPayment - networkCost
  const unitPriceLimit = recoverableBeforePurchase / totalPurchaseGeneration
  const xMaxUnit =
    unitPriceLimit > 0
      ? Math.max(unitPriceLimit * 1.5, currentPurchaseAverageUnit)
      : Math.max(currentPurchaseAverageUnit * 2, 1)
  const maxUnitPoint = Math.max(1, Math.floor(xMaxUnit))
  const points: SellPlanUnitPricePoint[] = []

  for (
    let purchaseAverageUnit = 0;
    purchaseAverageUnit <= maxUnitPoint;
    purchaseAverageUnit++
  ) {
    points.push({
      purchaseAverageUnit,
      totalProfit: Math.floor(
        recoverableBeforePurchase -
          totalPurchaseGeneration * purchaseAverageUnit
      ),
    })
  }

  const currentTotalProfit = Math.floor(
    recoverableBeforePurchase -
      totalPurchaseGeneration * currentPurchaseAverageUnit
  )
  const previousCustomerCharge = sumMonthNum(beforeCustomerCharge)
  const currentTotalProfitRatio =
    previousCustomerCharge === 0
      ? null
      : (currentTotalProfit / previousCustomerCharge) * 100

  return {
    points,
    currentPurchaseAverageUnit,
    currentTotalProfit,
    currentTotalProfitRatio,
    unitPriceLimit,
    xMaxUnit,
    feasible: currentTotalProfit >= 0,
  }
}
